import { Component, OnInit, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationManagerService } from 'src/app/shared/services/navigation-manager.service';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { SiteManagementService } from '../site-management.service';
import {
  IEventHistoryEvent,
  IEventHistoryFilterCriteria,
  IEventHistoryResponse,
} from '../site-management-interfaces';
import { NotificationService } from 'src/app/shared/errors/notification.service';

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.scss'],
})
export class ActivityComponent implements OnInit {
  @Input() masterCredential: any;
  @Input() selectedSite: any;
  @Input() public resetNavigation: any;

  expandedActivityId: string | null = null;

  // Event codes that should display description text
  private readonly DESCRIPTION_EVENT_CODES = [
    '0x07',
    '0x08',
    '0x09',
    '0x1c',
    '0x1d',
  ];

  // Event history data
  events: IEventHistoryEvent[] = [];
  totalRecords: number = 0;
  cursor: string[] | null = null;

  // Pagination and loading states
  readonly PAGE_SIZE: number = 10;
  filterCriteria: IEventHistoryFilterCriteria = {
    pagination: {
      size: this.PAGE_SIZE,
      cursor: null,
    },
  };
  isFetching: boolean = false;
  hasMoreData: boolean = true;
  loadingMore: boolean = false;
  isInitialLoad: boolean = true;

  constructor(
    private navigationManagerService: NavigationManagerService,
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private siteManagementService: SiteManagementService,
    private notificationService: NotificationService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.fetchEventHistory();
  }

  /**
   * @description Fetches event history by RFID card using badgeId and siteId
   * Supports pagination with cursor-based loading
   */
  fetchEventHistory(): void {
    if (this.isFetching) return;

    const badgeId = this.masterCredential?.badgeId;
    const siteId = this.selectedSite?.siteId;

    if (!badgeId || !siteId) {
      console.warn('Missing badgeId or siteId for fetching event history');
      return;
    }

    this.isFetching = true;

    this.siteManagementService
      .getEventHistoryByRFIDCard(badgeId, siteId, this.filterCriteria)
      .subscribe({
        next: (response: IEventHistoryResponse) => {
          if (response?.events) {
            // Append new events to existing list (for load more)
            this.addUniqueEvents(response.events);
            this.totalRecords = response.totalRecords || 0;

            // Update cursor for next page
            if (response.cursor) {
              this.cursor = response.cursor;
              this.filterCriteria.pagination.cursor = response.cursor;
            }

            // Check if there's more data to load
            this.hasMoreData =
              response.events.length > 0 &&
              response.cursor !== null &&
              this.events.length < this.totalRecords;
          } else {
            this.hasMoreData = false;
          }

          this.isFetching = false;
          this.loadingMore = false;
          this.isInitialLoad = false;
        },
        error: (err: Error) => {
          console.error('Error fetching event history:', err);
          this.notificationService.showError(err?.message);
          this.isFetching = false;
          this.loadingMore = false;
          this.hasMoreData = false;
          this.isInitialLoad = false;
        },
      });
  }

  /**
   * @description Adds unique events to the events array to avoid duplicates
   * @param newEvents The new events to add
   */
  private addUniqueEvents(newEvents: IEventHistoryEvent[]): void {
    const existingIds = new Set(this.events.map((event) => event.id));
    const uniqueEvents = newEvents.filter(
      (event) => !existingIds.has(event.id)
    );
    this.events.push(...uniqueEvents);
  }

  /**
   * @description Handler for infinite scroll event - loads more data
   */
  onScrollDown(): void {
    if (!this.isFetching && this.hasMoreData) {
      this.loadingMore = true;
      this.fetchEventHistory();
    }
  }

  /**
   * @description Resets the component state and reloads data
   */
  resetAndReload(): void {
    this.events = [];
    this.cursor = null;
    this.totalRecords = 0;
    this.hasMoreData = true;
    this.isFetching = false;
    this.loadingMore = false;
    this.isInitialLoad = true;
    this.filterCriteria = {
      pagination: {
        size: this.PAGE_SIZE,
        cursor: null,
      },
    };
    this.fetchEventHistory();
  }

  /**
   * @description Determines the severity class based on event properties
   * @param event The event to evaluate
   * @returns The severity class name
   */
  getSeverityClass(event: IEventHistoryEvent): string {
    const eventCode = (event.eventCode || '').toLowerCase();
    const alertCodes = CONSTANTS.ALERT_DEVICE_EVENT_CODE;
    const unlockCodes = CONSTANTS.UNLOCK_DEVICE_EVENT_CODE;
    const lockedCodes = CONSTANTS.LOCKED_UNIT_EVENT_CODE;
    const onlineCodes = CONSTANTS.ONLINE_DEVICE_EVENT_CODE;

    if (event.isIssue === true || alertCodes.includes(eventCode)) {
      return 'severity-critical';
    } else if (unlockCodes.includes(eventCode)) {
      return 'severity-success';
    } else if (lockedCodes.includes(eventCode)) {
      return 'severity-warning';
    } else if (onlineCodes.includes(eventCode)) {
      return 'severity-info';
    }

    return 'severity-warning';
  }

  /**
   * @description Gets the icon path based on event code
   * @param event The event to get icon for
   * @returns The icon path
   */
  getEventIcon(event: IEventHistoryEvent): string {
    const eventCode = (event.eventCode || '').toLowerCase();
    const alertCodes = CONSTANTS.ALERT_DEVICE_EVENT_CODE;
    const unlockCodes = CONSTANTS.UNLOCK_DEVICE_EVENT_CODE;
    const lockedCodes = CONSTANTS.LOCKED_UNIT_EVENT_CODE;
    const onlineCodes = CONSTANTS.ONLINE_DEVICE_EVENT_CODE;

    if (event.isIssue === true || alertCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.ALERT_ICON;
    } else if (unlockCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.UNLOCK_EVENT_ICON;
    } else if (lockedCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.LOCK_ICON;
    } else if (onlineCodes.includes(eventCode)) {
      return 'assets/svg/' + CONSTANTS.ONLINE_ICON;
    }

    return 'assets/svg/' + CONSTANTS.LOCK_ICON;
  }

  /**
   * @description Gets the display title for an event
   * @param event The event
   * @returns The formatted title
   */
  getEventTitle(event: IEventHistoryEvent): string {
    if (event.eventTitle) {
      return `${event.deviceName}: ${event.eventTitle}`;
    }
    return event.deviceName || 'Unknown Device';
  }

  /**
   * @description Gets the source display string for an event
   * Format:
   * - NFC: "source | cardName - badgeId"
   * - Other sources: "source | sourceName"
   * @param event The event
   * @returns The formatted source display string
   */
  getSourceDisplay(event: IEventHistoryEvent): string {
    const rawSource = event.source || '';
    const source =
      rawSource.toUpperCase() === 'MANUAL'
        ? this.translate.instant('MANUAL')
        : rawSource;
    const sourceName = event.sourceName?.trim();

    if (sourceName) {
      if (rawSource.toUpperCase() !== 'NFC') {
        return `${source} | ${sourceName}`;
      }

      const [badgeIdPart = '', cardNamePart = ''] = sourceName
        .split('|')
        .map((part) => part.trim());
      const rawCardName = cardNamePart || sourceName;
      const cardName = event.isMaster
        ? `${this.translate.instant('MASTER')} ${rawCardName}`
        : rawCardName;
      const badgeId = badgeIdPart || this.masterCredential?.badgeId || '';

      return badgeId
        ? `${source} | ${cardName} - ID.${badgeId}`
        : `${source} | ${cardName}`;
    }
    return source;
  }

  /**
   * @description Checks if extraDescription should be shown for this event
   * Shown for codes 0x07, 0x08, 0x09, 0x1c, 0x1d or abnormal/issue events
   */
  hasExtraDescription(event: IEventHistoryEvent): boolean {
    const eventCode = (event.eventCode || '').toLowerCase();
    return (
      this.DESCRIPTION_EVENT_CODES.includes(eventCode) || event.isIssue === true
    );
  }

  /**
   * @description Function used as the trackBy function for ngFor directive
   * @param index The index
   * @param event The event object
   * @returns The unique identifier
   */
  trackByEvent(index: number, event: IEventHistoryEvent): string {
    return event?.id || index.toString();
  }

  /**
   * @description This method navigates back to the master credential detail screen
   */
  navigateBack(): void {
    this.navigationManagerService.setStepSiteManager(
      CONSTANTS.MASTER_CREDENTIAL_DETAIL
    );
  }

  /**
   * @description This method toggles the dropdown for an activity
   * @param activityId The activity ID to toggle
   */
  toggleDropdown(activityId: string): void {
    if (this.expandedActivityId === activityId) {
      this.expandedActivityId = null;
    } else {
      this.expandedActivityId = activityId;
    }
  }

  /**
   * @description This method closes dialog
   */
  closeDialog(): void {
    this.resetNavigation();
    this.dialogRef.close();
  }
}
